import { TermsAndSubscription } from '../terms-and-subscription/TermsAndSubscription';
import { ComponentConfig, FieldLabel } from '@measured/puck';
import {
  MarginProps,
  marginFields,
} from '../../../page-builder/puck/reusable-props/margin';
import { QuillEditor } from '../../../components/common/quill-editor/quill-editor';
import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';
import { Switch, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { getIsEdit } from '../../utils/constants';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getQuillEditorText,
} from '../../utils/helper';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { paddingFields, PaddingProps } from '../../puck/reusable-props/padding';
import '../text-editor/style.scss';
import ColorPicker from '../../puck/reusable-props/ColorPicker';

export type ExtraTermProps = {
  viewMoreTerms: boolean;
  defaultCollapse: boolean;
  moreTerms: string;
};
export type TermsAndSubProps = {
  showHeader: boolean;
  headerContent: string;
  showAcknowledgeDropdown: boolean;
  acknowledgeContent: string;
  extraTerms: ExtraTermProps;
  border: IBorderValueProps;
  checkboxColor: string;
} & MarginProps &
  PaddingProps &
  showComponentProps;

export const TermsAndSub = ({
  useAppDispatch,
  useAppSelector,
}: ReduxDispatchProps &
  ReduxSelectorProps): ComponentConfig<TermsAndSubProps> => {
  return {
    label: (
      <CustomTooltip title="TermsAndSub">
        <span>Terms and Subscription VIP</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      showHeader: {
        type: 'radio',
        label: 'Show Header',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      headerContent: {
        ...QuillTextEditor('Header Content', true, 1).content,
      },
      showAcknowledgeDropdown: {
        type: 'radio',
        label: 'Show Dropdown Arrow',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      acknowledgeContent: {
        ...QuillTextEditor('Terms and Subscription', true, 2).content,
      },
      extraTerms: {
        type: 'custom',
        render: ({ value, onChange }) => {
          const handleChanges = (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange({ ...value, viewMoreTerms: e.target.checked });
          };
          const handleQuillChanges = (content: string) => {
            onChange({ ...value, moreTerms: content });
          };
          return (
            <>
              <div className="grid">
                <div className="flex justify-between items-center mb-3">
                  <p className="text-sm font-semibold"> Show Extra Terms</p>
                  <div className="flex gap-0.5 items-center">
                    <Tooltip
                      title={`Extra terms will be displayed when user clicks on "Learn More"`}
                      placement="top-start">
                      <InfoIcon fontSize="small" className="h-1 w-1" />
                    </Tooltip>
                    <Switch
                      checked={value?.viewMoreTerms}
                      onChange={handleChanges}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  </div>
                </div>
                {value?.viewMoreTerms && (
                  <div className="flex justify-between items-center mb-3">
                    <p className="text-sm font-semibold">Default collapse</p>
                    <div className="flex gap-0.5 items-center">
                      <Tooltip
                        title={
                          'Toggling this will either collapse or expand the Extra Terms by default.'
                        }
                        placement="top-start">
                        <InfoIcon fontSize="small" className="h-1 w-1" />
                      </Tooltip>
                      <Switch
                        checked={value?.defaultCollapse}
                        onChange={event =>
                          onChange({
                            ...value,
                            defaultCollapse: event.target.checked,
                          })
                        }
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    </div>
                  </div>
                )}
              </div>
              {value?.viewMoreTerms && (
                <FieldLabel label="Extra Terms" el="div">
                  <QuillEditor
                    value={value.moreTerms}
                    onChange={handleQuillChanges}
                    id={3}
                  />
                </FieldLabel>
              )}
            </>
          );
        },
      },
      checkboxColor: {
        type: 'custom',
        label: 'Checkbox Color',
        render: ({ value, onChange, field }) => {
          return (
            <ColorPicker
              value={value}
              onChange={onChange}
              label={field.label}
              tooltip={field.label}
            />
          );
        },
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      checkboxColor: '#4f46e5',
      ...borderProps,
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      showHeader: false,
      headerContent:
        '<h3 style="line-height:1; style="font-size: 14px;"><span>Header Content</span></h3>',
      showAcknowledgeDropdown: false,
      acknowledgeContent:
        '<p style="line-height: 1.5;"><span style="font-size: 14px;">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna</span></p>',
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      extraTerms: {
        viewMoreTerms: false,
        defaultCollapse: false,
        moreTerms:
          '<ul style="line-height: 1.5;"><li><span style="font-size: 12px;">The VIP Membership provides access to VIP Pricing on all products on the website, priority access to 24/7 customer support, free shipping on all membership portal orders, and exclusive new product releases.</span></li></ul><p><br></p><ul><li><span style="font-size: 12px; line-height: 1.5;">Automatic Billing. Your credit card or other payment method on file will be charged the VIP Membership fee of 14 dollars and 99 cents USD (exclusive of taxes) every 30 days without obtaining your further confirmation or revised payment information. The first charge will occur 12 days after purchase and then every 30 days after. You may be charged less, but never more than the Membership Fee without your revised consent.</span></li></ul><p><br></p><ul><li><span style="font-size: 12px; line-height: 1.5;">Cancellation. If you wish to cancel the VIP Membership at any time, you may do so by filling out the&nbsp;</span><span style="color: rgb(137, 8, 8); font-size: 12px; line-height: 1.5;">Cancel Membership</span><span style="font-size: 12px; line-height: 1.5;">&nbsp;form on our Website, by emailing us at support@valhallafallen.com or by calling us at +1 800-304-5759 at least 3 days before the next billing period with your full name and the email and physical address associated with your use of the Service. Your failure to cancel or your continued use of the VIP Membership reaffirms that Company is authorized to charge you for, and you are responsible for paying, all VIP Membership fees.</span></li></ul>',
      },
    },
    render: ({
      margin,
      showHeader,
      headerContent,
      showAcknowledgeDropdown,
      acknowledgeContent,
      extraTerms,
      showComponent,
      border,
      padding,
      editMode,
      checkboxColor,
    }) => {
      const isEditModeOn = getIsEdit();
      const hasSubscription = useAppSelector(
        state => state.checkout.hasSubscription,
      );
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const state = useAppSelector(state => state);
      const newInterpolation = getQuillEditorText(acknowledgeContent, state);

      return (
        <>
          {showComponent?.includes(currentDeviceType) &&
            (hasSubscription || isEditModeOn) && (
              <div
                className={`${border?.borderStyle?.[currentDeviceType]} text-editor-content`}
                style={{
                  ...marginStyles,
                  ...getBorderProps(border, currentDeviceType),
                }}>
                <TermsAndSubscription
                  showHeader={showHeader}
                  headerContent={headerContent}
                  showAcknowledgeDropdown={showAcknowledgeDropdown}
                  acknowledgeContent={newInterpolation}
                  checkboxColor={checkboxColor}
                  extraTerms={extraTerms}
                  padding={padding}
                  currentDeviceType={currentDeviceType}
                  useAppDispatch={useAppDispatch}
                  useAppSelector={useAppSelector}
                />
              </div>
            )}
        </>
      );
    },
  };
};
